import type { TFunction } from 'i18next';
import { z } from 'zod';

import { emailValidator } from '@/utils/validators/zod/emailValidator';
import { phoneNumberValidator } from '@/utils/validators/zod/phoneNumberValidator';
import { websiteValidator } from '@/utils/validators/zod/websiteValidator';

import {
	type HcoFieldKey,
	type HcpFieldKey,
	toItemFieldId
} from '../fields/registry';

import {
	addressItemSchema,
	affiliationItemSchema,
	licenseItemSchema
} from './collections.schema';
import { type EmailSequenceData, validateEmailSequence } from './email.schema';
import { localSpecialtyArraySchema } from './localSpecialty.schema';
import {
	collapsed,
	optionalBoolean,
	optionId,
	relaxUnavailable,
	requiredOptionId,
	requiredText,
	trimmed,
	yearValue
} from './primitives';

type EntitySchemaContext = {
	t: TFunction;
	countryId?: string | null;
	unavailable?: ReadonlySet<string>;
};

const hasAddressOrAffiliation = (data: unknown) => {
	const { addresses_V, parentHcos_V } = data as {
		addresses_V?: unknown[];
		parentHcos_V?: unknown[];
	};

	return (addresses_V?.length ?? 0) > 0 || (parentHcos_V?.length ?? 0) > 0;
};

const addressOrAffiliationIssue = (t: TFunction) => ({
	message: t('Affiliation or Address required'),
	path: ['addresses_V']
});

export const hcpNameShape = (t?: TFunction) => ({
	firstNameCda_V: requiredText(t, { collapse: true }),
	middleNameCda_V: collapsed(),
	lastNameCda_V: requiredText(t, { collapse: true }),
	prefixCda_V: trimmed(),
	suffixCda_V: trimmed()
});

export const hcoNameShape = (t?: TFunction) => ({
	hcoNameCda_V: requiredText(t)
});

const isItemUnavailable =
	(unavailable: ReadonlySet<string> | undefined, collectionKey: string) =>
	(key: string) =>
		!!unavailable?.has(toItemFieldId(collectionKey, key));

const collectionShape = (t: TFunction, unavailable?: ReadonlySet<string>) => ({
	parentHcos_V: z
		.array(
			affiliationItemSchema(t, isItemUnavailable(unavailable, 'parentHcos_V'))
		)
		.default([]),
	addresses_V: z
		.array(addressItemSchema(t, isItemUnavailable(unavailable, 'addresses_V')))
		.default([]),
	licenses_V: z
		.array(
			licenseItemSchema(t, {
				isUnavailable: isItemUnavailable(unavailable, 'licenses_V')
			})
		)
		.default([])
});

export const hcpEntityShape = (
	t: TFunction,
	unavailable?: ReadonlySet<string>
) =>
	relaxUnavailable(
		{
			...hcpNameShape(t),
			internationalName_V: trimmed(),

			primaryCountry_V: optionId(),
			hcpTypeCda_VId: requiredOptionId(t),
			hcpTypeLocal_VId: optionId(),
			degree1Cda_VId: optionId(),
			spec1Cda_VId: optionId(),
			allSpecLocal_VId: localSpecialtyArraySchema(t, { required: true }),
			statusCda_VId: optionId(),
			hcpStatus_VId: optionId(),
			prescriberCda_V: optionalBoolean(),
			telehealth_V: optionalBoolean(),
			inCurrentPecos_V: optionalBoolean(),

			nhidCda_V: trimmed(),
			nhid2_V: trimmed(),
			hcpTaxId_V: trimmed(),

			languageCda_VId: optionId(),
			gender_VId: optionId(),
			birthYear_V: yearValue(t),
			dataPrivacyOptOut_V: optionalBoolean(),
			gradSchool_V: trimmed(),
			gradYear_V: yearValue(t),

			officePhoneCda_V: phoneNumberValidator(),
			phone2_V: phoneNumberValidator(),
			faxCda_V: phoneNumberValidator(),
			emailCda_V: emailValidator(),
			email2_V: emailValidator(),
			email3_V: emailValidator(),
			email4_V: emailValidator(),
			email5_V: emailValidator(),
			websiteCda_V: websiteValidator(),

			...collectionShape(t, unavailable)
		} satisfies Record<HcpFieldKey, z.ZodType>,
		key => !!unavailable?.has(key)
	);

export const hcoEntityShape = (
	t: TFunction,
	unavailable?: ReadonlySet<string>
) =>
	relaxUnavailable(
		{
			...hcoNameShape(t),
			internationalName_V: trimmed(),

			primaryCountry_V: optionId(),
			hcoTypeCda_VId: requiredOptionId(t),
			hcoTypeLocal_VId: optionId(),
			academicStatus_VId: optionId(),
			hcoOwnership_VId: optionId(),
			businessType_VId: optionId(),
			allSpecLocal_VId: localSpecialtyArraySchema(t),
			statusCda_VId: optionId(),
			countHcp_VId: optionId(),
			countPrescriber_VId: optionId(),

			nhidCda_V: trimmed(),
			hcoTaxId_V: trimmed(),

			officePhoneCda_V: phoneNumberValidator(),
			faxCda_V: phoneNumberValidator(),
			emailCda_V: emailValidator(),
			websiteCda_V: websiteValidator(),

			typeOfHierarchy_VId: optionId(),

			...collectionShape(t, unavailable)
		} satisfies Record<HcoFieldKey, z.ZodType>,
		key => !!unavailable?.has(key)
	);

export const hcpEntitySchema = <Extra extends z.ZodRawShape = z.ZodRawShape>(
	{ t, countryId, unavailable }: EntitySchemaContext,
	extra?: Extra
) =>
	z
		.object({ ...hcpEntityShape(t, unavailable), ...(extra as Extra) })
		.refine(hasAddressOrAffiliation, addressOrAffiliationIssue(t))
		.superRefine((data, ctx) =>
			validateEmailSequence(t, countryId === 'us')(
				data as EmailSequenceData,
				ctx
			)
		);

export const hcoEntitySchema = <Extra extends z.ZodRawShape = z.ZodRawShape>(
	{ t, unavailable }: EntitySchemaContext,
	extra?: Extra
) =>
	z
		.object({ ...hcoEntityShape(t, unavailable), ...(extra as Extra) })
		.refine(hasAddressOrAffiliation, addressOrAffiliationIssue(t));

export const hcpNameSchema = (t?: TFunction) => z.object(hcpNameShape(t));
export const hcoNameSchema = (t?: TFunction) => z.object(hcoNameShape(t));

type HcpEntitySchema = ReturnType<typeof hcpEntitySchema<Record<never, never>>>;
type HcoEntitySchema = ReturnType<typeof hcoEntitySchema<Record<never, never>>>;

export type HcpEntityValuesIn = z.input<HcpEntitySchema>;
export type HcpEntityValuesOut = z.output<HcpEntitySchema>;
export type HcoEntityValuesIn = z.input<HcoEntitySchema>;
export type HcoEntityValuesOut = z.output<HcoEntitySchema>;
